import { captureException } from "@sentry/core";
import type { Doc } from "building-types/doc";
import type { Elevation } from "building-types/options/elevationOptionSet";
import type { OptionChoices } from "building-types/options/optionChoices";
import { archetypeOnlyOptions } from "building-types/options/optionDefinitions";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { optionSetSelectedOptionId } from "doc/options/optionSetTools";
import { docGetDefaultElevation, docGetElevation } from "./docGetElevations";
import { optionSetOptionIds } from "./optionSetBaseUtils";
import { SystemOptionSet } from "./SystemOptionSetEnum";

function reportInvalidResolvedElevationId(
  doc: Pick<Doc, "optionSets">,
  optionChoices: OptionChoices,
  resolvedId: string,
): void {
  const elevationOptionSet = doc.optionSets[SystemOptionSet.ELEVATION_STYLE];
  captureException(new Error(`Active elevation id "${resolvedId}" does not map to a valid elevation option`), {
    extra: {
      resolvedElevationId: resolvedId,
      elevationOptionChoice: optionChoices[SystemOptionSet.ELEVATION_STYLE] ?? null,
      elevationDefaultValue: elevationOptionSet?.defaultValue ?? null,
    },
    tags: {
      docOption: "activeElevation",
    },
  });
}

/**
 * Resolves the elevation option ID from the current option choices, falling back to the option
 * set's configured default. Unlike {@link getActiveElevation}, this does not verify that the ID
 * still exists in the elevation option set — callers that need a usable elevation should prefer
 * {@link getActiveElevation}.
 */
export function getActiveElevationId(optionSets: Table<DocOptionSet>, optionChoices: OptionChoices) {
  return optionSetSelectedOptionId(archetypeOnlyOptions(optionSets), optionChoices, SystemOptionSet.ELEVATION_STYLE);
}

/**
 * Returns an elevation only when `id` maps to a present, non-disabled option. Option template
 * overrides can leave stale IDs in `optionChoices` or `defaultValue` after elevations are
 * renamed or removed, so callers must not assume a resolved ID is still valid.
 */
function elevationWithValidOption(doc: Pick<Doc, "optionSets">, id: string | null | undefined): Elevation | undefined {
  if (!id) {
    return undefined;
  }

  const elevation = docGetElevation(doc, id);
  if (elevation?.option) {
    return elevation;
  }

  return undefined;
}

/** Last-resort fallback when both the resolved choice and configured default point at missing options. */
function getFirstValidElevation(doc: Pick<Doc, "optionSets">): Elevation | undefined {
  const elevationOptionSet = doc.optionSets[SystemOptionSet.ELEVATION_STYLE];
  if (!elevationOptionSet) {
    return undefined;
  }

  for (const optionId of optionSetOptionIds(elevationOptionSet, true)) {
    const elevation = elevationWithValidOption(doc, optionId);
    if (elevation) {
      return elevation;
    }
  }

  return undefined;
}

/**
 * Returns the elevation currently in effect for a doc and option choice set.
 *
 * Resolution order:
 * 1. The choice resolved by {@link getActiveElevationId}, if it still exists in the option set
 * 2. The elevation option set's `defaultValue`, if valid
 * 3. The first non-disabled elevation option
 *
 * `optionSets` should include merged option template overrides (as produced by building
 * generation) so defaults reflect the effective plan configuration.
 */
export function getActiveElevation(doc: Pick<Doc, "optionSets">, optionChoices: OptionChoices): Elevation | undefined {
  const resolvedId = getActiveElevationId(doc.optionSets, optionChoices);
  const resolvedElevation = elevationWithValidOption(doc, resolvedId);
  if (resolvedElevation) {
    return resolvedElevation;
  }

  if (resolvedId) {
    reportInvalidResolvedElevationId(doc, optionChoices, resolvedId);
  }

  const defaultElevation = docGetDefaultElevation(doc);
  if (defaultElevation?.option) {
    return defaultElevation;
  }

  return getFirstValidElevation(doc);
}

/** Convenience wrapper around {@link getActiveElevation} for display labels. */
export function getActiveElevationName(doc: Pick<Doc, "optionSets">, optionChoices: OptionChoices): string | undefined {
  const activeElevation = getActiveElevation(doc, optionChoices);
  return activeElevation?.option.name;
}
